// KidHome — Jul 2026 (Randy). The kid-mode surface: family agenda, my chores
// with streaks, and an ask box (parent-set daily allowance; adds always go to
// the parent's approval inbox). Auth = kid token in localStorage.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarThemeProvider, useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KIND_EMOJI = { event: "📅", task: "✅", routine: "⭐", ping: "🔔" };

function kidApi() {
  const t = localStorage.getItem("mysilo_kid_token");
  return axios.create({ baseURL: API, headers: { Authorization: `Bearer ${t}` } });
}

// Feb 2026 (Randy) — starter suggestions for the "Suggest to my parent"
// form. One tap fills in the title (and default time for routines/pings).
// Categorized by proposal kind so the starter matches what the kid is
// about to submit.
const KID_PROPOSE_STARTERS = {
  task: [
    { emoji: "🧹", title: "Clean my room" },
    { emoji: "🍽️", title: "Set the table" },
    { emoji: "🗑️", title: "Take out the trash" },
    { emoji: "🧺", title: "Fold my laundry" },
    { emoji: "🐕", title: "Walk the dog" },
    { emoji: "🌱", title: "Water the plants" },
  ],
  routine: [
    { emoji: "📚", title: "Read for 20 min", time: "19:00" },
    { emoji: "🎹", title: "Practice piano", time: "17:00" },
    { emoji: "🦷", title: "Brush my teeth", time: "20:30" },
    { emoji: "🏃", title: "Exercise 15 min", time: "07:00" },
    { emoji: "🛏️", title: "Make my bed", time: "07:30" },
    { emoji: "🥛", title: "Feed the pet", time: "07:15" },
  ],
  ping: [
    { emoji: "📝", title: "Did I finish my homework?", time: "18:00" },
    { emoji: "🎒", title: "Is my backpack ready for tomorrow?", time: "20:00" },
    { emoji: "💧", title: "Have I had enough water today?", time: "16:00" },
    { emoji: "📞", title: "Should I call Grandma?", time: "17:30" },
    { emoji: "🧠", title: "What am I grateful for today?", time: "20:30" },
  ],
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmt12 = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};
const dayLabel = (dstr, todayStr) => {
  if (dstr === todayStr) return "Today";
  const d = new Date(dstr + "T12:00:00");
  const t = new Date(todayStr + "T12:00:00");
  if ((d - t) / 86400000 === 1) return "Tomorrow";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
};

export default function KidHome() {
  // pt89U Stage-C — pull theme_map early via /kid/me and seed a lightweight
  // provider around the render tree. `skipFetch` keeps the provider from
  // trying `/silos/{id}` (kid tokens can't hit that endpoint).
  const [themeMap, setThemeMap] = useState({});
  return (
    <CalendarThemeProvider siloId={null} skipFetch initialMap={themeMap}>
      <KidHomeInner onThemeMap={setThemeMap} />
    </CalendarThemeProvider>
  );
}

function KidHomeInner({ onThemeMap }) {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [cal, setCal] = useState(null);
  const [rewards, setRewards] = useState(null);
  // pt89U Stage-C — themeable strings for the kid surface.
  const tGreetingSuffix = useThemeLabel(T.THEME_KEY_KID_GREETING_SUFFIX);
  const tJobsToday      = useThemeLabel(T.THEME_KEY_KID_JOBS_TODAY);
  const tJobsEmpty      = useThemeLabel(T.THEME_KEY_KID_JOBS_EMPTY);
  const tProposeTitle   = useThemeLabel(T.THEME_KEY_KID_PROPOSE_TITLE);
  const tProposeIntro   = useThemeLabel(T.THEME_KEY_KID_PROPOSE_INTRO);
  const tBadges         = useThemeLabel(T.THEME_KEY_KID_BADGES);
  const tRewardsShop    = useThemeLabel(T.THEME_KEY_KID_REWARDS_SHOP);
  const tLeaderboard    = useThemeLabel(T.THEME_KEY_KID_LEADERBOARD);
  const tPendingHeading = useThemeLabel(T.THEME_KEY_KID_PENDING_HEADING);
  const tWhatsHappening = useThemeLabel(T.THEME_KEY_KID_WHATS_HAPPENING);
  const tAskTitle       = useThemeLabel(T.THEME_KEY_KID_ASK_TITLE);
  const [q, setQ] = useState("");
  const [chat, setChat] = useState([]);
  const [asking, setAsking] = useState(false);
  const [pushOn, setPushOn] = useState(localStorage.getItem("mysilo_kid_push") === "1");
  // Feb 2026 (Randy) — Rewards shop collapsible + persisted per-kid (device
  // may host multiple kid profiles). Default OPEN on first visit; whatever
  // the kid picks (open/closed) sticks until they change it.
  const [rewardsOpen, setRewardsOpen] = useState(true);
  useEffect(() => {
    if (!me?.id) return;
    try {
      const v = localStorage.getItem(`mysilo_kid_rewards_open_${me.id}`);
      setRewardsOpen(v === null ? true : v === "1");
    } catch { /* ignore */ }
  }, [me?.id]);
  useEffect(() => {
    if (!me?.id) return;
    try { localStorage.setItem(`mysilo_kid_rewards_open_${me.id}`, rewardsOpen ? "1" : "0"); }
    catch { /* ignore */ }
  }, [rewardsOpen, me?.id]);
  // Feb 2026 (Randy) — kid propose-event form. Kids can suggest a chore /
  // routine / ping to their parent; on approve it auto-assigns back to
  // this kid (calendar_module.approve_event auto-fills assignee_user_id
  // from proposed_by).
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposeKind, setProposeKind] = useState("task");
  const [proposeTitle, setProposeTitle] = useState("");
  const [proposeTime, setProposeTime] = useState("");
  const [proposeBusy, setProposeBusy] = useState(false);
  const chatEnd = useRef(null);

  const loadCal = () => {
    const today = new Date();
    const end = new Date(today); end.setDate(end.getDate() + 6);
    kidApi().get("/kid/calendar", { params: { start: iso(today), end: iso(end) } })
      .then((r) => setCal(r.data)).catch(() => {});
    kidApi().get("/kid/rewards").then((r) => setRewards(r.data)).catch(() => {});
  };
  useEffect(() => {
    // Feb 28 2026 v5 (Randy pt-kid-portal-persistent-session) — Part A:
    // read the kid token from `?k=<tok>` in the URL FIRST, fall back
    // to localStorage. iOS 16.4+ partitions PWA localStorage away
    // from Safari's, so a home-screen icon launch has empty storage
    // on cold boot. The URL is the only bulletproof carrier. Whenever
    // a URL token is present, hydrate it into localStorage so
    // subsequent same-context calls (via kidApi()) keep working.
    let bootTok = null;
    try {
      bootTok = new URLSearchParams(window.location.search).get("k");
      if (bootTok) localStorage.setItem("mysilo_kid_token", bootTok);
    } catch { /* ignore */ }
    if (!localStorage.getItem("mysilo_kid_token")) { nav("/", { replace: true }); return; }
    kidApi().get("/kid/me").then((r) => {
      setMe(r.data);
      // pt89U Stage-C — push the silo's theme_map up to the provider.
      const tm = r.data?.silo?.theme_map;
      if (tm && typeof tm === "object" && onThemeMap) onThemeMap(tm);
    }).catch((err) => {
      // Feb 28 2026 v5 (Randy pt-kid-portal-persistent-session) — Part B:
      // ONLY kick out on a real dead-session 401 (revoked link / token
      // expired). Transient 5xx / network flakes (LTE hiccup during
      // pull-to-refresh, backend cold-start) previously nuked the
      // token AND kicked her to marketing — one flake = permanent
      // session death. Now those flakes keep the token and just
      // surface a retry toast.
      if (err?.response?.status === 401) {
        localStorage.removeItem("mysilo_kid_token");
        nav("/", { replace: true });
        return;
      }
      toast.error("Connection hiccup — pull to refresh in a moment.");
    });
    loadCal();
    // eslint-disable-next-line
  }, []);
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [chat]);

  const byId = useMemo(() => Object.fromEntries((cal?.events || []).map((e) => [e.id, e])), [cal]);
  const todayStr = cal?.today;
  const choresToday = useMemo(() =>
    (cal?.occurrences || []).filter((o) => o.date === todayStr && (o.kind === "routine" || o.kind === "task")),
    [cal, todayStr]);
  // Feb 2026 (Randy) — streak-in-danger detection. A routine's streak dies
  // if today's occurrence isn't checked off before the day ends. If any of
  // today's routine chores are ≥3-day streaks AND still not done, we mark
  // the header chip in "danger" state (teenager-feel red pulse — no big
  // scary banner).
  const streakInDanger = useMemo(() =>
    choresToday.some((o) => o.kind === "routine" && !o.done && (cal?.streaks?.[o.event_id] || 0) >= 3),
    [choresToday, cal]);
  const agenda = useMemo(() => {
    const groups = {};
    (cal?.occurrences || []).filter((o) => o.kind === "event" || o.kind === "routine").forEach((o) => {
      (groups[o.date] = groups[o.date] || []).push(o);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [cal]);

  const toggleChore = async (o) => {
    // Feb 28 2026 v5.4 (Randy pt-kid-portal-tap-parity) — mirror the
    // Master Dashboard's tap semantics so kid check-offs behave the
    // same way parents see them:
    //   • empty  → tap sends done=true → backend flips to done (Confirm
    //              Mode OFF) or pending (Confirm Mode ON).
    //   • pending → tap sends done=false → cancels the pending. Lets
    //              the kid undo an accidental check while a parent
    //              hasn't approved yet.
    //   • done   → no-op. Once approved, only a parent can unmark
    //              (from the Master Dashboard with PIN).
    if (o.done) return;
    const nextDone = !o.pending;
    try {
      await kidApi().post(`/kid/events/${o.event_id}/done`, { date: o.date, done: nextDone });
      loadCal();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't do that."); }
  };

  const claimReward = async (r) => {
    if (r.cost_points > (rewards?.points_spendable || 0)) { toast.error("Not enough points yet — keep earning!"); return; }
    try {
      await kidApi().post(`/kid/rewards/${r.id}/redeem`);
      toast.success("Sent to a parent for approval! 🎉");
      loadCal();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't claim that."); }
  };

  const ask = async () => {
    const question = q.trim();
    if (!question || asking) return;
    setChat((c) => [...c, { who: "me", text: question }]);
    setQ(""); setAsking(true);
    try {
      const r = await kidApi().post("/kid/ask", { question });
      setChat((c) => [...c, { who: "silo", text: r.data.answer }]);
      setMe((m) => (m ? { ...m, asks_left: r.data.asks_left } : m));
      loadCal();
    } catch (e) {
      setChat((c) => [...c, { who: "silo", text: e.response?.data?.detail || "Oops, something went wrong — try again!" }]);
    } finally { setAsking(false); }
  };

  const switchPerson = () => {
    const sid = me?.silo?.id;
    localStorage.removeItem("mysilo_kid_token");
    nav(sid ? `/family/${sid}` : "/", { replace: true });
  };

  const enablePush = async () => {    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        toast.error("This device doesn't support notifications."); return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Notifications were blocked — check the device settings."); return; }
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { data: keyResp } = await kidApi().get("/kid/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyResp.public_key),
      });
      const json = sub.toJSON();
      await kidApi().post("/kid/push-subscribe", { endpoint: sub.endpoint, keys: json.keys, user_agent: navigator.userAgent });
      localStorage.setItem("mysilo_kid_push", "1");
      setPushOn(true);
      toast.success("All set — your reminders will ping this device! 🔔");
    } catch {
      toast.error("Couldn't turn on notifications — try again.");
    }
  };

  // Feb 2026 (Randy) — submit a chore/routine/ping proposal to the parent.
  const submitPropose = async () => {
    const title = proposeTitle.trim();
    if (!title) { toast.error("Give it a short name first."); return; }
    setProposeBusy(true);
    try {
      const body = { kind: proposeKind, title };
      if (proposeTime) body.time = proposeTime;
      if (proposeKind === "routine") body.freq = "daily";
      if (proposeKind === "ping") body.ping_question = title;
      await kidApi().post("/kid/propose-event", body);
      toast.success("Sent to your parent for approval! 🎉");
      setProposeTitle(""); setProposeTime(""); setProposeOpen(false);
      loadCal();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send — try again.");
    } finally { setProposeBusy(false); }
  };

  if (!me) return <div className="min-h-[100dvh] bg-[#FDFCF8] flex items-center justify-center text-sm text-[#6B6A68]">Loading…</div>;

  return (
    <div className="min-h-[100dvh] bg-[#FDFCF8]" data-testid="kid-home">
      <header className="px-5 pt-6 pb-4 max-w-2xl mx-auto flex items-center gap-3">
        <span className="text-4xl">{me.kid?.emoji}</span>
        <div className="flex-1">
          <h1 className="font-serif-display text-2xl text-[#1C1B1A]" data-testid="kid-home-greeting">Hi {me.kid?.name}!</h1>
          <div className="text-[11px] text-[#6B6A68]">{me.silo?.name} · {tGreetingSuffix}</div>
        </div>
        {rewards && rewards.best_streak > 0 && (
          <span
            title={streakInDanger
              ? `${rewards.best_streak}-day streak — check off today's chores before midnight or it dies!`
              : `${rewards.best_streak}-day streak — keep it going`}
            data-testid="kid-streak-chip"
            className={`text-xs px-3 py-1.5 rounded-full border font-medium shrink-0 flex items-center gap-1 ${
              streakInDanger
                ? "bg-[#FBEBE4] border-[#EACAB8] text-[#A55A48]"
                : "bg-black/5 border-black/10 text-[#1C1B1A]"
            }`}>
            <span className={streakInDanger ? "animate-pulse" : ""}>🔥</span>
            <span className="tabular-nums">{rewards.best_streak}</span>
            {streakInDanger && <span className="text-[10px] font-bold ml-0.5" data-testid="kid-streak-danger">!</span>}
          </span>
        )}
        {rewards && (
          <span className="text-xs px-3 py-1.5 rounded-full bg-[#F8F1E2] border border-[#EAD9AE] text-[#8A6A24] font-medium shrink-0" data-testid="kid-points-chip">
            ⭐ {rewards.points} pts
          </span>
        )}
        <button onClick={switchPerson} className="text-[10px] underline text-[#6B6A68] hover:text-[#1C1B1A]" data-testid="kid-switch-person">
          switch person
        </button>
      </header>

      <main className="siloart-copy px-5 pb-24 max-w-2xl mx-auto space-y-5">
        {/* turn on reminders (Randy — chore pings reach the kid's own device) */}
        {!pushOn && (
          <button onClick={enablePush} data-testid="kid-enable-push"
            className="w-full flex items-center gap-3 text-left px-4 py-3 rounded-2xl border-2 border-[#B9D4BE] bg-[#F4F8F5] hover:bg-[#EAF3EC] transition-colors">
            <span className="text-2xl">🔔</span>
            <span className="flex-1">
              <span className="block text-sm font-medium text-[#1C1B1A]">Want your reminders on this device?</span>
              <span className="block text-[11px] text-[#6B6A68]">Your chores and events will ping you right here — tap to turn on.</span>
              <span className="block text-[10px] text-[#4A5D4E] mt-0.5">Tip: after this, add My Silo Kid to your home screen (Share → Add to Home Screen) so notifications keep working when the app is closed.</span>
            </span>
            <span className="text-xs px-3 py-1.5 rounded-lg bg-[#4A5D4E] text-white shrink-0">Turn on</span>
          </button>
        )}

        {/* my chores today */}
        <section className="bg-white border-2 border-[#F0E7D2] rounded-2xl p-4" data-testid="kid-chores">
          <h2 className="text-base font-medium text-[#1C1B1A] mb-2">⭐ {tJobsToday}</h2>
          {choresToday.length === 0 ? (
            <p className="text-sm text-[#6B6A68] italic">{tJobsEmpty}</p>
          ) : (
            <div className="space-y-2">
              {choresToday.map((o, i) => {
                const ev = byId[o.event_id] || {};
                const mine = !ev.assignee_user_id || ev.assignee_user_id === cal?.kid_uid;
                const streak = cal?.streaks?.[o.event_id];
                // Feb 28 2026 v5.4 (Randy pt-kid-portal-tap-parity) —
                // three-state checkbox parity with MasterDashboardChoreCard:
                // empty (grey) → pending (red border + red check) → done
                // (solid green + white check). Pending WINS over done
                // for the fresh-mark-after-stale-done edge case.
                const isPending = !!o.pending && !o.done;
                const isDone = !isPending && !!o.done;
                const boxCls = isPending
                  ? "bg-transparent border-2 border-[#E86A5C] text-[#E86A5C]"
                  : isDone
                    ? "bg-[#4A5D4E] border-2 border-[#4A5D4E] text-white"
                    : "bg-transparent border-2 border-[#C9C7C1] text-transparent";
                const rowCls = isPending
                  ? "bg-[#FDF3F1] border-[#F1C5BE]"
                  : isDone
                    ? "bg-[#EAF3EC] border-[#B9D4BE]"
                    : "bg-[#FDFCF8] border-[#E4E2DC]";
                return (
                  <button key={i} onClick={() => mine && toggleChore(o)} disabled={!mine || isDone}
                    data-testid={`kid-chore-${o.event_id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 text-left transition-colors ${rowCls} ${!mine ? "opacity-50" : isDone ? "" : "hover:border-[#4A5D4E]"}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm shrink-0 ${boxCls}`}>
                      {(isDone || isPending) ? "✓" : ""}
                    </span>
                    <span className={`flex-1 text-sm ${isDone ? "line-through text-[#6B6A68]" : "text-[#1C1B1A]"}`}>
                      {ev.title}{!mine && ev.assignee_name ? ` (${ev.assignee_name}'s)` : ""}
                      {isPending && <span className="ml-2 text-[10px] uppercase tracking-wider text-[#E86A5C]">waiting on approval</span>}
                    </span>
                    {streak > 0 && <span className="text-xs shrink-0" data-testid={`kid-streak-${o.event_id}`}>🔥 {streak}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Feb 2026 (Randy) — Suggest to Parent. Kids can propose a chore /
            routine / ping; on approve it lands on the family calendar with
            them as the assignee (pings route to their device alone). */}
        <section className="bg-white border-2 border-[#DDE7DD] rounded-2xl p-4" data-testid="kid-propose">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-base font-medium text-[#1C1B1A] flex-1">💡 {tProposeTitle}</h2>
            {!proposeOpen && (
              <button onClick={() => setProposeOpen(true)} data-testid="kid-propose-open"
                className="text-xs px-3 py-1.5 rounded-lg bg-[#4A5D4E] text-white hover:bg-[#3A4A3E]">
                + Suggest
              </button>
            )}
            {proposeOpen && (
              <button onClick={() => { setProposeOpen(false); setProposeTitle(""); setProposeTime(""); }}
                data-testid="kid-propose-cancel"
                className="text-xs text-[#6B6A68] hover:text-[#1C1B1A]">Cancel</button>
            )}
          </div>
          {!proposeOpen ? (
            <p className="text-[11px] text-[#6B6A68] leading-relaxed">
              {tProposeIntro}
            </p>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-1.5" data-testid="kid-propose-kind">
                {[
                  { v: "task", l: "Task", d: "once" },
                  { v: "routine", l: "Routine", d: "daily" },
                  { v: "ping", l: "Ping", d: "reminder" },
                ].map((k) => (
                  <button key={k.v} onClick={() => setProposeKind(k.v)}
                    data-testid={`kid-propose-kind-${k.v}`}
                    className={`flex-1 text-[11px] px-2 py-1.5 rounded-lg border-2 ${proposeKind === k.v ? "bg-[#4A5D4E] text-white border-[#4A5D4E]" : "bg-white text-[#1C1B1A] border-[#E4E2DC]"}`}>
                    {k.l} <span className="opacity-70 text-[9px]">· {k.d}</span>
                  </button>
                ))}
              </div>
              {/* Feb 2026 (Randy) — starter list of age-appropriate ideas so
                  kids don't stare at an empty input. Tap fills in the title
                  (and time when it makes sense) so they can just hit Send.
                  Feb 26, 2026 (Randy pt-kid-propose-themed-starters) — if
                  the silo has been re-themed the Re-Themer emitted 6-8
                  themed starters into `theme_meta.starters`; those take
                  priority over the hardcoded per-kind defaults. One shared
                  flat list — kind is chosen separately via the toggle. */}
              {!proposeTitle && (() => {
                const themed = Array.isArray(me?.silo?.theme_meta?.starters) && me.silo.theme_meta.starters.length > 0
                  ? me.silo.theme_meta.starters
                  : null;
                const chips = themed || KID_PROPOSE_STARTERS[proposeKind];
                return (
                  <div className="space-y-1" data-testid="kid-propose-starters">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Not sure what to suggest?</div>
                    <div className="flex flex-wrap gap-1.5">
                      {chips.map((s) => (
                        <button key={s.title} onClick={() => { setProposeTitle(s.title); if (s.time) setProposeTime(s.time); }}
                          data-testid={`kid-propose-starter-${s.title.toLowerCase().replace(/\W+/g,'-')}`}
                          className="text-[11px] px-2 py-1 rounded-full border-2 border-[#E4E2DC] bg-white hover:border-[#4A5D4E] hover:bg-[#F4F8F5] transition-colors">
                          {s.emoji} {s.title}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <input value={proposeTitle} onChange={(e) => setProposeTitle(e.target.value)}
                data-testid="kid-propose-title" autoFocus maxLength={80}
                placeholder={proposeKind === "ping" ? "e.g., What's my homework tonight?" : "e.g., Feed the dog"}
                className="w-full px-3 py-2 border-2 border-[#E4E2DC] rounded-lg text-sm bg-white focus:outline-none focus:border-[#4A5D4E]" />
              {proposeKind !== "task" && (
                <div className="flex items-center gap-1.5 text-[11px] text-[#6B6A68]">
                  <span>Time (optional):</span>
                  <input type="time" value={proposeTime} onChange={(e) => setProposeTime(e.target.value)}
                    data-testid="kid-propose-time"
                    className="px-2 py-1 border-2 border-[#E4E2DC] rounded text-xs bg-white" />
                </div>
              )}
              <button onClick={submitPropose} disabled={proposeBusy || !proposeTitle.trim()}
                data-testid="kid-propose-submit"
                className="w-full text-sm px-3 py-2 rounded-lg bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] disabled:opacity-50">
                {proposeBusy ? "Sending…" : "Send for approval →"}
              </button>
            </div>
          )}
        </section>

        {/* my badges */}
        {rewards && (
          <section className="bg-white border-2 border-[#F0E7D2] rounded-2xl p-4" data-testid="kid-badges">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-base font-medium text-[#1C1B1A] flex-1">🏆 {tBadges}</h2>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {rewards.badges.map((b) => (
                <div key={b.id} title={b.earned ? b.label : `${b.label} — ${b.progress}/${b.need}`}
                  data-testid={`kid-badge-${b.id}`}
                  className={`flex flex-col items-center gap-1 rounded-xl border-2 p-2 text-center ${b.earned ? "bg-[#FDF9EF] border-[#EAD9AE]" : "bg-[#FAFAF8] border-[#EDECE8] opacity-45 grayscale"}`}>
                  <span className="text-2xl">{b.emoji}</span>
                  <span className="text-[9px] leading-tight text-[#1C1B1A]">{b.label}</span>
                  {!b.earned && <span className="text-[8px] text-[#6B6A68]">{b.progress}/{b.need}</span>}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[#6B6A68] mt-2">Every job you check off earns 10 points — keep your streaks alive to unlock the fire badges!</p>
          </section>
        )}

        {/* reward shop */}
        {rewards && (rewards.store || []).length > 0 && (
          <section className="bg-white border-2 border-[#F5DFD7] rounded-2xl p-4" data-testid="kid-rewards-shop">
            <button type="button"
              onClick={() => setRewardsOpen((v) => !v)}
              data-testid="kid-rewards-shop-toggle"
              className={`w-full flex items-center gap-2 text-left ${rewardsOpen ? "mb-2" : ""}`}
              aria-expanded={rewardsOpen}>
              <span className="text-base font-medium text-[#1C1B1A] flex-1">🎁 {tRewardsShop}</span>
              <span className="text-xs px-3 py-1.5 rounded-full bg-[#F8F1E2] border border-[#EAD9AE] text-[#8A6A24] font-medium" data-testid="kid-spendable-chip">
                ⭐ {rewards.points_spendable} to spend
              </span>
              <span className={`text-lg text-[#8A6A24] w-6 text-center transition-transform ${rewardsOpen ? "rotate-0" : "-rotate-90"}`} aria-hidden="true">▾</span>
            </button>
            {rewardsOpen && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  {rewards.store.map((r) => {
                    const afford = r.cost_points <= (rewards.points_spendable || 0);
                    return (
                      <button key={r.id} onClick={() => claimReward(r)} disabled={!afford}
                        data-testid={`kid-reward-${r.id}`}
                        className={`flex items-center gap-2 text-left p-3 rounded-xl border-2 transition-colors ${afford ? "border-[#F0E7D2] hover:border-[#C16E5A] bg-[#FDFCF8]" : "border-[#EDECE8] bg-[#FAFAF8] opacity-55 cursor-not-allowed"}`}>
                        <span className="text-2xl">{r.emoji}</span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-[#1C1B1A] truncate">{r.name}</span>
                          <span className="text-[11px] text-[#8A6A24] font-medium">{r.cost_points} pts</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                {(rewards.my_redemptions || []).filter((x) => x.status === "pending").length > 0 && (
                  <div className="mt-3 bg-[#FDF9EF] border-2 border-[#EAD9AE] rounded-xl p-2.5" data-testid="kid-pending-redemptions">
                    <div className="text-xs font-medium text-[#8A6A24] mb-1">⏳ {tPendingHeading}</div>
                    {rewards.my_redemptions.filter((x) => x.status === "pending").map((x) => (
                      <div key={x.id} className="text-sm text-[#1C1B1A]">• {x.reward_emoji} {x.reward_name}</div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* family leaderboard */}
        {rewards && (rewards.leaderboard || []).length > 1 && (
          <section className="bg-white border-2 border-[#EAD9AE] rounded-2xl p-4" data-testid="kid-leaderboard">
            <h2 className="text-base font-medium text-[#1C1B1A] mb-2">🏆 {tLeaderboard}</h2>
            <div className="space-y-1.5">
              {rewards.leaderboard.map((m, i) => (
                <div key={m.member_id} className="flex items-center gap-2 text-sm" data-testid={`kid-leaderboard-${m.member_id}`}>
                  <span className="w-6 text-center">{["🥇", "🥈", "🥉"][i] || `${i + 1}.`}</span>
                  <span className="text-lg leading-none">{m.emoji}</span>
                  <span className="flex-1 truncate text-[#1C1B1A]">{m.name}{m.member_id === me.kid?.id ? " (you)" : ""}</span>
                  {m.best_streak > 0 && <span className="text-[11px] text-[#C16E5A]">🔥{m.best_streak}</span>}
                  <span className="text-xs font-medium text-[#8A6A24] tabular-nums">{m.points} pts</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* waiting for a parent's OK */}
        {(cal?.pending || []).length > 0 && (
          <section className="bg-[#FDF9EF] border-2 border-[#EAD9AE] rounded-2xl p-4" data-testid="kid-pending">
            <h2 className="text-sm font-medium text-[#8A6A24] mb-1.5">⏳ {tPendingHeading}</h2>
            {cal.pending.map((p) => (
              <div key={p.id} className="text-sm text-[#1C1B1A]">• {p.title}</div>
            ))}
          </section>
        )}

        {/* family agenda */}
        <section className="bg-white border-2 border-[#E3EFF7] rounded-2xl p-4" data-testid="kid-agenda">
          <h2 className="text-base font-medium text-[#1C1B1A] mb-2">📅 {tWhatsHappening}</h2>
          {agenda.length === 0 ? (
            <p className="text-sm text-[#6B6A68] italic">Nothing planned this week yet.</p>
          ) : agenda.map(([date, items]) => (
            <div key={date} className="mb-2.5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1">{dayLabel(date, todayStr)}</div>
              {items.map((o, i) => {
                const ev = byId[o.event_id] || {};
                return (
                  <div key={i} className="flex items-center gap-2 text-sm text-[#1C1B1A] py-0.5">
                    <span>{KIND_EMOJI[o.kind] || "📌"}</span>
                    <span className="flex-1 truncate">{ev.title}</span>
                    {o.start_time && <span className="text-[11px] text-[#6B6A68] shrink-0">{fmt12(o.start_time)}</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </section>

        {/* ask box */}
        <section className="bg-white border-2 border-[#E9E2F2] rounded-2xl p-4" data-testid="kid-ask-section">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="text-base font-medium text-[#1C1B1A] flex-1">💬 {tAskTitle}</h2>
            <span className="text-[10px] text-[#6B6A68]" data-testid="kid-asks-left">{me.asks_left} question{me.asks_left === 1 ? "" : "s"} left today</span>
          </div>
          {chat.length > 0 && (
            <div className="space-y-2 mb-3 max-h-[300px] overflow-y-auto pr-1">
              {chat.map((m, i) => (
                <div key={i} className={`text-sm px-3 py-2 rounded-xl max-w-[90%] whitespace-pre-wrap ${m.who === "me" ? "bg-[#4A5D4E] text-white ml-auto" : "bg-[#F0F1EE] text-[#1C1B1A]"}`}>
                  {m.text}
                </div>
              ))}
              {asking && <div className="text-sm px-3 py-2 rounded-xl bg-[#F0F1EE] text-[#6B6A68] w-16 animate-pulse">…</div>}
              <div ref={chatEnd} />
            </div>
          )}
          <div className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
              placeholder="What's happening this weekend?"
              disabled={me.asks_left <= 0}
              data-testid="kid-ask-input"
              className="flex-1 px-3 py-2.5 border-2 border-[#E4E2DC] rounded-xl text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E] disabled:opacity-50" />
            <button onClick={ask} disabled={asking || !q.trim() || me.asks_left <= 0} data-testid="kid-ask-send"
              className="px-4 py-2.5 rounded-xl bg-[#4A5D4E] text-white text-sm disabled:opacity-50">Ask</button>
          </div>
          <p className="text-[10px] text-[#6B6A68] mt-2">
            If you ask to add something to the calendar, a parent gets to say OK first. 👍
          </p>
        </section>
      </main>
    </div>
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}
